import React, { useEffect, useRef } from 'react';
import {
  GoogleMap,
  OVERLAY_MOUSE_TARGET,
  OverlayViewF,
  useGoogleMap,
} from '@react-google-maps/api';
import { BoxSeam } from 'react-bootstrap-icons';
import { useGoogleMaps } from '../google_map/GoogleMapsContext';
import {
  getDriverInitials,
  getDriverTruckTypes,
  hasActiveDelivery,
  isLocationFresh,
} from './fleetTrackingUtils';
import TruckTypeIcon from './TruckTypeIcon';

const DEFAULT_CENTER = { lat: 49.8951, lng: -97.1384 };
const DEFAULT_ZOOM = 12;

const mapOptions = {
  clickableIcons: false,
  disableDefaultUI: true,
  fullscreenControl: false,
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  zoomControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#ffffff' }],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#65748b' }],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#f3f6f9' }],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#cfe8f4' }],
    },
  ],
};

const markerOffset = () => ({ x: -29, y: -26 });

const FitFleetBounds = ({
  points,
  request,
  sidebarLeftPadding,
  sidebarBottomPadding,
}) => {
  const map = useGoogleMap();
  const pointsRef = useRef(points);
  const sidebarLeftPaddingRef = useRef(sidebarLeftPadding);
  const sidebarBottomPaddingRef = useRef(sidebarBottomPadding);
  pointsRef.current = points;
  sidebarLeftPaddingRef.current = sidebarLeftPadding;
  sidebarBottomPaddingRef.current = sidebarBottomPadding;

  useEffect(() => {
    const currentPoints = pointsRef.current;
    if (!map || !window.google) return undefined;

    if (currentPoints.length === 0) {
      map.panTo(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return undefined;
    }

    if (currentPoints.length === 1) {
      map.panTo(currentPoints[0]);
      map.setZoom(14);
      return undefined;
    }

    const bounds = new window.google.maps.LatLngBounds();
    currentPoints.forEach((point) => bounds.extend(point));
    const mobile = window.innerWidth <= 720;
    map.fitBounds(bounds, {
      top: mobile ? 50 : 90,
      right: mobile ? 50 : 80,
      bottom: mobile ? sidebarBottomPaddingRef.current : 80,
      left: mobile ? 50 : sidebarLeftPaddingRef.current,
    });

    const listener = window.google.maps.event.addListenerOnce(
      map,
      'idle',
      () => {
        if (map.getZoom() > 15) map.setZoom(15);
      },
    );

    return () => window.google.maps.event.removeListener(listener);
  }, [map, request]);

  return null;
};

const FocusDriver = ({ position, request }) => {
  const map = useGoogleMap();
  const lastRequestRef = useRef(0);

  useEffect(() => {
    if (
      !map ||
      !position ||
      request === 0 ||
      lastRequestRef.current === request
    ) {
      return;
    }

    lastRequestRef.current = request;
    map.panTo(position);
    if (map.getZoom() < 14) map.setZoom(14);
  }, [map, position, request]);

  return null;
};

const FleetLiveMap = ({
  drivers,
  driverLocations,
  selectedDriverId,
  onSelectDriver,
  fitRequest,
  focusRequest,
  now,
  sidebarLeftPadding = 390,
  sidebarBottomPadding = 50,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const points = drivers
    .map((driver) => driverLocations[String(driver.id)])
    .filter(Boolean)
    .map(({ lat, lng }) => ({ lat, lng }));
  const selectedPosition = selectedDriverId
    ? driverLocations[String(selectedDriverId)]
    : null;

  if (loadError) {
    return (
      <div className="fleet-map-state" role="alert">
        <i className="fa fa-map-o" aria-hidden="true" />
        <h3>Map could not be loaded</h3>
        <p>Check the Google Maps key and refresh this page.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="fleet-map-state" aria-label="Loading fleet map">
        <span className="fleet-map-spinner" />
        <h3>Loading live map</h3>
        <p>Connecting to your fleet...</p>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerClassName="fleet-google-map"
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      options={mapOptions}
    >
      <FitFleetBounds
        points={points}
        request={fitRequest}
        sidebarLeftPadding={sidebarLeftPadding}
        sidebarBottomPadding={sidebarBottomPadding}
      />
      <FocusDriver position={selectedPosition} request={focusRequest} />

      {drivers.map((driver) => {
        const location = driverLocations[String(driver.id)];
        if (!location) return null;

        const selected = String(driver.id) === String(selectedDriverId);
        const fresh = isLocationFresh(location, now);
        const onDelivery = hasActiveDelivery(driver);
        const truckType = getDriverTruckTypes(driver)[0];
        const truckTypeLabel =
          truckType?.title || truckType?.type || 'Truck type not assigned';

        return (
          <OverlayViewF
            key={driver.id}
            position={location}
            mapPaneName={OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={markerOffset}
            zIndex={selected ? 40 : 20}
          >
            <button
              type="button"
              className={`fleet-map-driver-marker ${
                onDelivery ? 'on-delivery' : 'no-active-delivery'
              } ${fresh ? 'live' : 'stale'} ${selected ? 'selected' : ''}`}
              onClick={() => onSelectDriver(driver.id)}
              title={`${driver.name || `Driver #${driver.id}`} · ${
                truckTypeLabel
              } · ${fresh ? 'Live' : 'Stale location'} · ${
                onDelivery ? 'On delivery' : 'No active delivery'
              }`}
              aria-label={`Select driver ${driver.name || driver.id}`}
            >
              <span className="fleet-driver-pulse" />
              <span className="fleet-truck-marker-label">
                <TruckTypeIcon truckType={truckType} className="map-marker" />
                <span className="fleet-map-driver-avatar">
                  {getDriverInitials(driver.name)}
                  {driver.profile_photo && (
                    <img
                      className="fleet-map-driver-photo"
                      src={driver.profile_photo}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.hidden = true;
                      }}
                    />
                  )}
                </span>
              </span>
              <span className="fleet-driver-delivery-state" aria-hidden="true">
                {onDelivery ? (
                  <BoxSeam />
                ) : (
                  <span className="fleet-no-active-delivery-dash" />
                )}
              </span>
            </button>
          </OverlayViewF>
        );
      })}
    </GoogleMap>
  );
};

export default FleetLiveMap;
